import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import './DSOSidebar.css';

// Import icons
import { 
  FaHome, 
  FaUsers,
  FaUserTie,
  FaClipboardList,
  FaBoxes, // Or use FaBoxOpen if preferred
  FaMoneyBillWave,
  FaFileInvoice,
  FaCreditCard,
  FaChartBar,
  FaCog,
  FaChevronDown,
  FaChevronUp,
  FaPercentage,
  FaUserFriends
} from 'react-icons/fa';

const DSOSidebar = () => {
  const location = useLocation();
  const { getCurrentUser } = useAuth();
  const currentUser = getCurrentUser();
  const [myPayoutsExpanded, setMyPayoutsExpanded] = useState(false);
  const [agentPayoutsExpanded, setAgentPayoutsExpanded] = useState(false);

  // Define sidebar links with appropriate icons
  const sidebarLinks = [
    { path: '/dso/home', label: 'Home', icon: <FaHome size={17} /> },
    { path: '/dso/team', label: 'Team', icon: <FaUsers size={17} /> },
    { path: '/dso/leads', label: 'Leads', icon: <FaClipboardList size={17} /> },
    { path: '/dso/backoffice', label: 'Backoffice', icon: <FaUserTie size={17} /> },
    { path: '/dso/products', label: 'Products', icon: <FaBoxes size={17} /> }, 
    { path: '/dso/payouts/commissions', label: 'Commissions', icon: <FaPercentage size={17} /> },
  ];

  // Collapsible my payouts section
  const myPayoutsLinks = [
    { path: '/dso/payouts', label: 'Payouts', icon: <FaCreditCard size={15} /> },
    { path: '/dso/invoices', label: 'Invoices', icon: <FaFileInvoice size={15} /> },
  ];

  // Collapsible agent payouts section
  const agentPayoutsLinks = [
    { path: '/dso/agent-invoices', label: 'Invoices', icon: <FaFileInvoice size={15} /> },
  ];

  // Additional links after the collapsible section
  const additionalLinks = [
    { path: '/dso/settings', label: 'Settings', icon: <FaCog size={17} /> },
  ];

  // Check if any payouts child route is active
  const isMyPayoutsActive = location.pathname === '/dso/payouts' || location.pathname === '/dso/invoices';
  const isAgentPayoutsActive = location.pathname === '/dso/agent-invoices';

  // Toggle payouts sections
  const toggleMyPayouts = () => {
    setMyPayoutsExpanded(!myPayoutsExpanded);
  };
  
  const toggleAgentPayouts = () => {
    setAgentPayoutsExpanded(!agentPayoutsExpanded);
  };

  return (
    <div className="dso-sidebar">
      <div className="dso-sidebar-logo">
        <img src="/static/images/Wishfin-logo.png" alt="Wishfin" />
      </div>
      
      <div className="dso-sidebar-title">
        <h3>DSO Portal</h3>
      </div>
      
      <nav className="dso-sidebar-nav">
        <ul>
          {/* Main links */}
          {sidebarLinks.map((link) => (
            <li 
              key={link.path} 
              className={location.pathname === link.path ? 'active' : ''}
            >
              <Link to={link.path}>
                <span className="dso-sidebar-icon">{link.icon}</span>
                <span className="dso-sidebar-label">{link.label}</span>
              </Link>
            </li>
          ))}
          
          {/* Collapsible my payouts section */}
          <li className={`has-submenu ${isMyPayoutsActive || myPayoutsExpanded ? 'active' : ''}`}>
            <div className="dso-sidebar-menu-item" onClick={toggleMyPayouts}>
              <span className="dso-sidebar-icon">
                <FaMoneyBillWave size={17} />
              </span>
              <span className="dso-sidebar-label">My Payouts</span>
              <span className="dso-submenu-toggle">
                {myPayoutsExpanded ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
              </span>
            </div>
            
            {/* My Payouts Submenu */}
            <ul className={`dso-submenu ${myPayoutsExpanded || isMyPayoutsActive ? 'expanded' : ''}`}>
              {myPayoutsLinks.map((link) => (
                <li 
                  key={link.path} 
                  className={location.pathname === link.path ? 'active' : ''}
                >
                  <Link to={link.path}>
                    <span className="dso-sidebar-icon">{link.icon}</span>
                    <span className="dso-sidebar-label">{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </li>
          
          {/* Collapsible agent payouts section */}
          <li className={`has-submenu ${isAgentPayoutsActive || agentPayoutsExpanded ? 'active' : ''}`}>
            <div className="dso-sidebar-menu-item" onClick={toggleAgentPayouts}>
              <span className="dso-sidebar-icon">
                <FaUserFriends size={17} />
              </span>
              <span className="dso-sidebar-label">Agent Invoices</span>
              <span className="dso-submenu-toggle">
                {agentPayoutsExpanded ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
              </span>
            </div>
            
            {/* Agent Payouts Submenu */}
            <ul className={`dso-submenu ${agentPayoutsExpanded || isAgentPayoutsActive ? 'expanded' : ''}`}>
              {agentPayoutsLinks.map((link) => (
                <li 
                  key={link.path} 
                  className={location.pathname === link.path ? 'active' : ''}
                >
                  <Link to={link.path}>
                    <span className="dso-sidebar-icon">{link.icon}</span>
                    <span className="dso-sidebar-label">{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </li>
          
          {/* Additional links */}
          {additionalLinks.map((link) => (
            <li 
              key={link.path} 
              className={location.pathname.startsWith(link.path) ? 'active' : ''}
            >
              <Link to={link.path}>
                <span className="dso-sidebar-icon">{link.icon}</span>
                <span className="dso-sidebar-label">{link.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      
      <div className="dso-sidebar-user-status">
        <div className="dso-sidebar-status">
          <div className="status-indicator online"></div>
          <span>Active</span>
        </div>
      </div>
    </div>
  );
};

export default DSOSidebar;
